import React, { ButtonHTMLAttributes, AnchorHTMLAttributes, ReactNode, forwardRef } from 'react';
import styles from './button.module.css';
import { Link } from '@/util/react-router-dom';
import clsx from 'clsx';
import { IconName, MaterialSymbolIcon } from '@/components/material-symbol-link';
import { Loader } from './loaders/loader';

export type ButtonColor = 'primary' | 'accent';
export type ButtonVariant = 'filled' | 'outlined' | 'textOnly' | 'link' | 'none';
export type ButtonSize = 'S' | 'M' | 'L';

export interface CommonButtonProps {
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  className?: string;
  children: ReactNode;
  fullWidth?: boolean;
  leftIcon?: IconName;
  rightIcon?: IconName;
  disabled?: boolean;
  loading?: boolean;
}

type CommonButtonPropsKeys = keyof CommonButtonProps;

export interface ButtonLinkProps
  extends CommonButtonProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, CommonButtonPropsKeys> {
  as: 'a';
  to: string;
  state?: unknown;
  target?: string;
}

export interface RegularButtonProps
  extends CommonButtonProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, CommonButtonPropsKeys> {
  as?: 'button';
}
export type ButtonProps = RegularButtonProps | ButtonLinkProps;

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      variant = 'filled',
      color = 'primary',
      size = 'L',
      className = '',
      children,
      fullWidth = false,
      leftIcon,
      rightIcon,
      disabled,
      loading,
      ...rest
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;

    const baseStyles = clsx(
      styles.button,
      styles[`${variant}Button`],
      styles[`${color}Button`],
      styles[`button${size}`],
    );

    const buttonStyles = fullWidth ? { width: '100%' } : {};

    if (rest.as === 'a') {
      const { to, onClick, ...restProps } = rest;
      return (
        <Link
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
          className={clsx(baseStyles, className, isDisabled && styles.disabled)}
          style={buttonStyles}
          to={to}
          aria-disabled={isDisabled ? 'true' : undefined}
          onClick={(event) => {
            if (isDisabled) {
              event.preventDefault();
              event.stopPropagation();
              return;
            }
            onClick?.(event);
          }}
          {...restProps}
        >
          {leftIcon && <MaterialSymbolIcon name={leftIcon} className={styles.icon} />}
          <span>{children}</span>
          {rightIcon && <MaterialSymbolIcon name={rightIcon} className={styles.icon} />}
        </Link>
      );
    }

    const { type = 'button', ...restProps } = rest;

    return (
      <button
        ref={ref as React.ForwardedRef<HTMLButtonElement>}
        className={clsx(baseStyles, className)}
        style={buttonStyles}
        type={type}
        disabled={isDisabled}
        {...restProps}
      >
        {loading ? (
          <Loader type="button" />
        ) : (
          <>
            {leftIcon && <MaterialSymbolIcon name={leftIcon} className={styles.icon} />}
            <span>{children}</span>
            {rightIcon && <MaterialSymbolIcon name={rightIcon} className={styles.icon} />}
          </>
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';
